import React, { useState, useContext } from 'react';
import * as Switch from '@radix-ui/react-switch';
import { toast } from 'sonner';
import { motion, AnimatePresence } from 'framer-motion';
import AccountLinking from '../components/AccountLinking';
import PersonalCliqq from '../components/PersonalCliqq';
import UserHistory from '../components/UserHistory';
import PartnershipManager from '../components/PartnershipManager';
import GroupAvailability from '../components/GroupAvailability';
import FloatingInput from '../components/ui/FloatingInput';
import ConfirmDialog from '../components/ui/ConfirmDialog';
import { hardRedirect } from '../utils/navigation';
import { AuthContext } from '../contexts/AuthContext';
import api, { apiErrorMessage } from '../services/api';

/* ------------------------------------------------------------------ */
/*  SVG Icon components (Heroicons-style, inline)                     */
/* ------------------------------------------------------------------ */
const IconUser = () => (
  <svg className="w-5 h-5" fill="none" stroke="currentColor" strokeWidth={1.5} viewBox="0 0 24 24" aria-hidden="true">
    <path strokeLinecap="round" strokeLinejoin="round" d="M15.75 6a3.75 3.75 0 11-7.5 0 3.75 3.75 0 017.5 0zM4.501 20.118a7.5 7.5 0 0114.998 0A17.933 17.933 0 0112 21.75c-2.676 0-5.216-.584-7.499-1.632z" />
  </svg>
);
const IconLink = () => (
  <svg className="w-5 h-5" fill="none" stroke="currentColor" strokeWidth={1.5} viewBox="0 0 24 24" aria-hidden="true">
    <path strokeLinecap="round" strokeLinejoin="round" d="M13.19 8.688a4.5 4.5 0 011.242 7.244l-4.5 4.5a4.5 4.5 0 01-6.364-6.364l1.757-1.757m9.86-2.54a4.5 4.5 0 00-1.242-7.244l-4.5-4.5a4.5 4.5 0 00-6.364 6.364L4.343 8.28" />
  </svg>
);
const IconStar = () => (
  <svg className="w-5 h-5" fill="none" stroke="currentColor" strokeWidth={1.5} viewBox="0 0 24 24" aria-hidden="true">
    <path strokeLinecap="round" strokeLinejoin="round" d="M11.48 3.499a.562.562 0 011.04 0l2.125 5.111a.563.563 0 00.475.345l5.518.442c.499.04.701.663.321.988l-4.204 3.602a.563.563 0 00-.182.557l1.285 5.385a.562.562 0 01-.84.61l-4.725-2.885a.563.563 0 00-.586 0L6.982 20.54a.562.562 0 01-.84-.61l1.285-5.386a.562.562 0 00-.182-.557l-4.204-3.602a.563.563 0 01.321-.988l5.518-.442a.563.563 0 00.475-.345L11.48 3.5z" />
  </svg>
);
const IconCalendar = () => (
  <svg className="w-5 h-5" fill="none" stroke="currentColor" strokeWidth={1.5} viewBox="0 0 24 24" aria-hidden="true">
    <path strokeLinecap="round" strokeLinejoin="round" d="M6.75 3v2.25M17.25 3v2.25M3 18.75V7.5a2.25 2.25 0 012.25-2.25h13.5A2.25 2.25 0 0121 7.5v11.25m-18 0A2.25 2.25 0 005.25 21h13.5A2.25 2.25 0 0021 18.75m-18 0v-7.5A2.25 2.25 0 015.25 9h13.5A2.25 2.25 0 0121 11.25v7.5" />
  </svg>
);
const IconBell = () => (
  <svg className="w-5 h-5" fill="none" stroke="currentColor" strokeWidth={1.5} viewBox="0 0 24 24" aria-hidden="true">
    <path strokeLinecap="round" strokeLinejoin="round" d="M14.857 17.082a23.848 23.848 0 005.454-1.31A8.967 8.967 0 0118 9.75V9A6 6 0 006 9v.75a8.967 8.967 0 01-2.312 6.022c1.733.64 3.56 1.085 5.455 1.31m5.714 0a24.255 24.255 0 01-5.714 0m5.714 0a3 3 0 11-5.714 0" />
  </svg>
);
const IconShield = () => (
  <svg className="w-5 h-5" fill="none" stroke="currentColor" strokeWidth={1.5} viewBox="0 0 24 24" aria-hidden="true">
    <path strokeLinecap="round" strokeLinejoin="round" d="M9 12.75L11.25 15 15 9.75m-3-7.036A11.959 11.959 0 013.598 6 11.99 11.99 0 003 9.749c0 5.592 3.824 10.29 9 11.623 5.176-1.332 9-6.03 9-11.622 0-1.31-.21-2.571-.598-3.751h-.152c-3.196 0-6.1-1.248-8.25-3.285z" />
  </svg>
);
const IconHandshake = () => (
  <svg className="w-5 h-5" fill="none" stroke="currentColor" strokeWidth={1.5} viewBox="0 0 24 24" aria-hidden="true">
    <path strokeLinecap="round" strokeLinejoin="round" d="M15 19.128a9.38 9.38 0 002.625.372 9.337 9.337 0 004.121-.952 4.125 4.125 0 00-7.533-2.493M15 19.128v-.003c0-1.113-.285-2.16-.786-3.07M15 19.128v.106A12.318 12.318 0 018.624 21c-2.331 0-4.512-.645-6.374-1.766l-.001-.109a6.375 6.375 0 0111.964-3.07M12 6.375a3.375 3.375 0 11-6.75 0 3.375 3.375 0 016.75 0zm8.25 2.25a2.625 2.625 0 11-5.25 0 2.625 2.625 0 015.25 0z" />
  </svg>
);
const IconChart = () => (
  <svg className="w-5 h-5" fill="none" stroke="currentColor" strokeWidth={1.5} viewBox="0 0 24 24" aria-hidden="true">
    <path strokeLinecap="round" strokeLinejoin="round" d="M3 13.125C3 12.504 3.504 12 4.125 12h2.25c.621 0 1.125.504 1.125 1.125v6.75C7.5 20.496 6.996 21 6.375 21h-2.25A1.125 1.125 0 013 19.875v-6.75zM9.75 8.625c0-.621.504-1.125 1.125-1.125h2.25c.621 0 1.125.504 1.125 1.125v11.25c0 .621-.504 1.125-1.125 1.125h-2.25a1.125 1.125 0 01-1.125-1.125V8.625zM16.5 4.125c0-.621.504-1.125 1.125-1.125h2.25C20.496 3 21 3.504 21 4.125v15.75c0 .621-.504 1.125-1.125 1.125h-2.25a1.125 1.125 0 01-1.125-1.125V4.125z" />
  </svg>
);

/* ------------------------------------------------------------------ */
/*  Toggle component using Radix Switch                               */
/* ------------------------------------------------------------------ */
function Toggle({ checked, onCheckedChange, label, description }) {
  return (
    <div className="flex items-center justify-between py-3">
      <div className="pr-4">
        <p className="text-body font-medium text-surface-800">{label}</p>
        {description && <p className="text-body-sm text-surface-500 mt-0.5">{description}</p>}
      </div>
      <Switch.Root
        checked={checked}
        onCheckedChange={onCheckedChange}
        className="w-11 h-6 bg-surface-200 rounded-full relative data-[state=checked]:bg-primary-500 transition-colors duration-200 outline-none focus-visible:ring-2 focus-visible:ring-primary-500/40 focus-visible:ring-offset-2 cursor-pointer"
      >
        <Switch.Thumb className="block w-5 h-5 bg-white rounded-full shadow-soft-sm transition-transform duration-200 translate-x-0.5 will-change-transform data-[state=checked]:translate-x-[22px]" />
      </Switch.Root>
    </div>
  );
}

/* ------------------------------------------------------------------ */
/*  Main SettingsPage                                                  */
/* ------------------------------------------------------------------ */
function SettingsPage() {
  const { currentUser } = useContext(AuthContext);
  const [activeTab, setActiveTab] = useState('profile');

  /* ----- Profile form state (controlled) ----- */
  const [displayName, setDisplayName] = useState(currentUser?.username || currentUser?.Username || '');
  const [email, setEmail] = useState(currentUser?.email || currentUser?.Email || '');
  const [bio, setBio] = useState(currentUser?.bio || currentUser?.Bio || '');
  const [savingProfile, setSavingProfile] = useState(false);

  /* ----- Notification state (controlled) ----- */
  const [emailNotifs, setEmailNotifs] = useState(true);
  const [pushNotifs, setPushNotifs] = useState(true);
  const [friendReqNotifs, setFriendReqNotifs] = useState(true);
  const [eventReminders, setEventReminders] = useState(true);
  const [savingNotifs, setSavingNotifs] = useState(false);

  /* ----- Privacy state (controlled) ----- */
  const [profileVisibility, setProfileVisibility] = useState('everyone');
  const [historyVisibility, setHistoryVisibility] = useState('everyone');
  // eslint-disable-next-line no-unused-vars -- setter returns when the privacy
  // endpoints exist; see handleSavePrivacy.
  const [savingPrivacy, setSavingPrivacy] = useState(false);

  /* ----- Change password state ----- */
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [changingPassword, setChangingPassword] = useState(false);

  /* ----- Delete account dialog ----- */
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);

  const isBusiness =
    currentUser?.account_type === 'business' ||
    currentUser?.AccountType === 'business' ||
    currentUser?.accountType === 'business';

  /* ----- Tab definitions ----- */
  const tabs = [
    { id: 'profile', label: 'Profile Settings', icon: <IconUser /> },
    { id: 'account-linking', label: 'Account Linking', icon: <IconLink /> },
    { id: 'personal-cliqq', label: 'Personal CliQQ', icon: <IconStar /> },
    { id: 'history', label: 'Event History', icon: <IconCalendar /> },
    { id: 'notifications', label: 'Notifications', icon: <IconBell /> },
    { id: 'privacy', label: 'Privacy & Security', icon: <IconShield /> },
  ];

  if (isBusiness) {
    tabs.push(
      { id: 'partnerships', label: 'Partnerships', icon: <IconHandshake /> },
      { id: 'group-availability', label: 'Group Availability', icon: <IconChart /> }
    );
  }

  /* ----- Handlers ----- */
  // NOTE on endpoints: the user routes are `/api/users/:id`, so posting to a
  // literal path like `/api/users/profile` makes Gin bind id="profile" and the
  // handler rejects it with 403 "You can only update your own profile". Always
  // interpolate the real user id.
  const userId = currentUser?.id || currentUser?.ID;

  const handleSaveProfile = async () => {
    if (!userId) {
      toast.error('You must be signed in to update your profile');
      return;
    }
    setSavingProfile(true);
    try {
      // Email is intentionally omitted: UpdateUser does not accept email
      // changes (there is no re-verification flow yet).
      await api.put(`/api/users/${userId}`, {
        username: displayName,
        bio,
      });
      toast.success('Profile saved successfully');
    } catch (err) {
      toast.error(apiErrorMessage(err, 'Failed to save profile'));
    } finally {
      setSavingProfile(false);
    }
  };

  const handleSaveNotifications = async () => {
    setSavingNotifs(true);
    try {
      // PUT /api/notifications/settings takes an array of per-type rows, each
      // carrying its own channel flags -- not a flat object of booleans.
      await api.put('/api/notifications/settings', [
        {
          notification_type: 'friend_request',
          is_enabled: friendReqNotifs,
          push_enabled: pushNotifs,
          email_enabled: emailNotifs,
        },
        {
          notification_type: 'event_reminder',
          is_enabled: eventReminders,
          push_enabled: pushNotifs,
          email_enabled: emailNotifs,
        },
      ]);
      toast.success('Notification preferences saved');
    } catch (err) {
      toast.error(apiErrorMessage(err, 'Failed to save notification preferences'));
    } finally {
      setSavingNotifs(false);
    }
  };

  // TODO(backend): profile/history visibility has no server-side support yet --
  // models.User has no visibility columns and GET /api/users/:id is public.
  // Wire this up as part of the "Private Profile Pages" work; until then don't
  // fire a request that is guaranteed to fail and imply the setting was saved.
  const handleSavePrivacy = async () => {
    toast.info('Privacy controls are not available yet — coming in a future update.');
  };

  // TODO(backend): no 2FA endpoint or model exists yet.
  const handleEnable2FA = async () => {
    toast.info('Two-factor authentication is not available yet.');
  };

  const handleChangePassword = async () => {
    setChangingPassword(true);
    try {
      await api.post('/api/users/change-password', {
        current_password: currentPassword,
        new_password: newPassword,
      });
      toast.success('Your password has been updated');
      setCurrentPassword('');
      setNewPassword('');
    } catch (err) {
      toast.error(apiErrorMessage(err, 'Failed to update password'));
    } finally {
      setChangingPassword(false);
    }
  };

  const handleDeleteAccount = async () => {
    if (!userId) {
      toast.error('You must be signed in to delete your account');
      return;
    }
    try {
      await api.delete(`/api/users/${userId}`);
      toast.success('Account deleted');
      hardRedirect('/');
    } catch (err) {
      toast.error(apiErrorMessage(err, 'Failed to delete account'));
    }
  };

  /* ----- Tab content renderer ----- */
  const renderTabContent = () => {
    switch (activeTab) {
      case 'profile':
        return (
          <motion.div
            key="profile"
            initial={{ opacity: 0, y: 6 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.2 }}
            className="bg-white rounded-2xl shadow-soft-sm border border-surface-200 p-6 sm:p-8"
          >
            <h2 className="text-h2 text-surface-900 mb-1">Profile Settings</h2>
            <p className="text-body-sm text-surface-500 mb-6">Manage how others see you on CliQQ</p>

            <div className="space-y-5">
              <FloatingInput
                label="Display Name"
                name="displayName"
                value={displayName}
                onChange={(e) => setDisplayName(e.target.value)}
              />

              <FloatingInput
                label="Email"
                name="email"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
              />

              <div className="relative">
                <textarea
                  id="bio"
                  name="bio"
                  rows={4}
                  value={bio}
                  onChange={(e) => setBio(e.target.value)}
                  placeholder=" "
                  className="peer w-full bg-surface-50 border border-surface-200 rounded-xl text-surface-900 pt-5 pb-2 px-4 focus:ring-2 focus:bg-white focus:outline-none focus:ring-primary-500/20 focus:border-primary-500 transition-all duration-200 text-body placeholder-transparent resize-none"
                />
                <label
                  htmlFor="bio"
                  className="absolute left-4 transition-all duration-200 pointer-events-none select-none peer-placeholder-shown:top-4 peer-placeholder-shown:text-body peer-placeholder-shown:text-surface-400 peer-focus:top-2 peer-focus:text-caption peer-focus:text-primary-600 top-2 text-caption text-surface-500"
                >
                  Bio
                </label>
              </div>

              <div className="flex justify-end pt-2">
                <button
                  onClick={handleSaveProfile}
                  disabled={savingProfile}
                  className="px-6 py-2.5 bg-primary-500 text-white text-body-sm font-semibold rounded-xl hover:bg-primary-600 focus:outline-none focus:ring-2 focus:ring-primary-500/40 focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed transition-colors duration-200"
                >
                  {savingProfile ? 'Saving...' : 'Save Changes'}
                </button>
              </div>
            </div>
          </motion.div>
        );

      case 'account-linking':
        return <AccountLinking />;

      case 'personal-cliqq':
        return <PersonalCliqq />;

      case 'history':
        return <UserHistory userId={currentUser?.id} />;

      case 'partnerships':
        return <PartnershipManager />;

      case 'group-availability':
        return <GroupAvailability groupId={currentUser?.organizationId} />;

      case 'notifications':
        return (
          <motion.div
            key="notifications"
            initial={{ opacity: 0, y: 6 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.2 }}
            className="bg-white rounded-2xl shadow-soft-sm border border-surface-200 p-6 sm:p-8"
          >
            <h2 className="text-h2 text-surface-900 mb-1">Notification Settings</h2>
            <p className="text-body-sm text-surface-500 mb-6">Choose how you want to be notified</p>

            <div className="divide-y divide-surface-100">
              <Toggle
                checked={emailNotifs}
                onCheckedChange={setEmailNotifs}
                label="Email Notifications"
                description="Receive email updates about your events"
              />
              <Toggle
                checked={pushNotifs}
                onCheckedChange={setPushNotifs}
                label="Push Notifications"
                description="Get notified about event updates"
              />
              <Toggle
                checked={friendReqNotifs}
                onCheckedChange={setFriendReqNotifs}
                label="Friend Requests"
                description="Notifications when someone sends a friend request"
              />
              <Toggle
                checked={eventReminders}
                onCheckedChange={setEventReminders}
                label="Event Reminders"
                description="Reminders before events start"
              />
            </div>

            <div className="flex justify-end pt-6">
              <button
                onClick={handleSaveNotifications}
                disabled={savingNotifs}
                className="px-6 py-2.5 bg-primary-500 text-white text-body-sm font-semibold rounded-xl hover:bg-primary-600 focus:outline-none focus:ring-2 focus:ring-primary-500/40 focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed transition-colors duration-200"
              >
                {savingNotifs ? 'Saving...' : 'Save Preferences'}
              </button>
            </div>
          </motion.div>
        );

      case 'privacy':
        return (
          <motion.div
            key="privacy"
            initial={{ opacity: 0, y: 6 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.2 }}
            className="bg-white rounded-2xl shadow-soft-sm border border-surface-200 p-6 sm:p-8"
          >
            <h2 className="text-h2 text-surface-900 mb-1">Privacy & Security</h2>
            <p className="text-body-sm text-surface-500 mb-6">Control who sees your information</p>

            <div className="space-y-6">
              {/* Profile Visibility */}
              <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2">
                <div>
                  <p className="text-body font-medium text-surface-800">Profile Visibility</p>
                  <p className="text-body-sm text-surface-500">Who can see your profile</p>
                </div>
                <select
                  value={profileVisibility}
                  onChange={(e) => setProfileVisibility(e.target.value)}
                  className="bg-surface-50 border border-surface-200 rounded-xl px-4 py-2.5 text-body-sm text-surface-700 focus:ring-2 focus:ring-primary-500/20 focus:border-primary-500 focus:outline-none transition-all duration-200"
                  aria-label="Profile visibility"
                >
                  <option value="everyone">Everyone</option>
                  <option value="friends">Friends Only</option>
                  <option value="private">Private</option>
                </select>
              </div>

              {/* History Visibility */}
              <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2">
                <div>
                  <p className="text-body font-medium text-surface-800">Event History Visibility</p>
                  <p className="text-body-sm text-surface-500">Who can see events you have attended</p>
                </div>
                <select
                  value={historyVisibility}
                  onChange={(e) => setHistoryVisibility(e.target.value)}
                  className="bg-surface-50 border border-surface-200 rounded-xl px-4 py-2.5 text-body-sm text-surface-700 focus:ring-2 focus:ring-primary-500/20 focus:border-primary-500 focus:outline-none transition-all duration-200"
                  aria-label="Event history visibility"
                >
                  <option value="everyone">Everyone</option>
                  <option value="friends">Friends Only</option>
                  <option value="private">Private</option>
                </select>
              </div>

              <div className="flex justify-end">
                <button
                  onClick={handleSavePrivacy}
                  disabled={savingPrivacy}
                  className="px-6 py-2.5 bg-primary-500 text-white text-body-sm font-semibold rounded-xl hover:bg-primary-600 focus:outline-none focus:ring-2 focus:ring-primary-500/40 focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed transition-colors duration-200"
                >
                  {savingPrivacy ? 'Saving...' : 'Save Privacy Settings'}
                </button>
              </div>

              {/* Change password */}
              <div className="border-t border-surface-200 pt-6">
                <p className="text-body font-medium text-surface-800 mb-1">Change Password</p>
                <p className="text-body-sm text-surface-500 mb-4">
                  You&apos;ll need your current password to set a new one.
                </p>
                <div className="space-y-4 max-w-md">
                  <FloatingInput
                    name="current_password"
                    type="password"
                    label="Current password"
                    value={currentPassword}
                    onChange={(e) => setCurrentPassword(e.target.value)}
                    autoComplete="current-password"
                  />
                  <FloatingInput
                    name="new_password"
                    type="password"
                    label="New password"
                    value={newPassword}
                    onChange={(e) => setNewPassword(e.target.value)}
                    autoComplete="new-password"
                  />
                  <button
                    onClick={handleChangePassword}
                    disabled={changingPassword || !currentPassword || !newPassword}
                    className="px-5 py-2.5 bg-primary-500 text-white text-body-sm font-semibold rounded-xl hover:bg-primary-600 focus:outline-none focus:ring-2 focus:ring-primary-500/40 focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed transition-colors duration-200"
                  >
                    {changingPassword ? 'Updating...' : 'Update Password'}
                  </button>
                </div>
              </div>

              {/* 2FA */}
              <div className="border-t border-surface-200 pt-6 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
                <div>
                  <p className="text-body font-medium text-surface-800">Two-Factor Authentication</p>
                  <p className="text-body-sm text-surface-500">Add an extra layer of security to your account</p>
                </div>
                <button
                  onClick={handleEnable2FA}
                  className="px-5 py-2.5 bg-primary-500 text-white text-body-sm font-semibold rounded-xl hover:bg-primary-600 focus:outline-none focus:ring-2 focus:ring-primary-500/40 focus:ring-offset-2 transition-colors duration-200 whitespace-nowrap"
                >
                  Enable
                </button>
              </div>

              {/* Delete Account */}
              <div className="border-t border-surface-200 pt-6">
                <div className="bg-error-50 rounded-xl p-4 sm:p-5 border border-error-500/10">
                  <h3 className="text-body font-semibold text-error-600 mb-1">Danger Zone</h3>
                  <p className="text-body-sm text-surface-600 mb-4">
                    Permanently delete your account and all associated data. This action cannot be undone.
                  </p>
                  <button
                    onClick={() => setDeleteDialogOpen(true)}
                    className="px-5 py-2.5 bg-error-500 text-white text-body-sm font-semibold rounded-xl hover:bg-error-600 focus:outline-none focus:ring-2 focus:ring-error-500/40 focus:ring-offset-2 transition-colors duration-200"
                  >
                    Delete Account
                  </button>
                </div>
              </div>
            </div>

            <ConfirmDialog
              open={deleteDialogOpen}
              onOpenChange={setDeleteDialogOpen}
              title="Delete your account?"
              description="This will permanently remove your profile, events, and all associated data. This action cannot be undone."
              confirmLabel="Delete Account"
              destructive
              onConfirm={handleDeleteAccount}
            />
          </motion.div>
        );

      default:
        return (
          <div className="bg-white rounded-2xl shadow-soft-sm border border-surface-200 p-6 sm:p-8">
            <h2 className="text-h2 text-surface-900 mb-2">Settings</h2>
            <p className="text-body text-surface-500">Select a category from the sidebar to view settings.</p>
          </div>
        );
    }
  };

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
      {/* Page header */}
      <div className="mb-8">
        <h1 className="text-h1 text-surface-900 mb-1">Settings</h1>
        <p className="text-body text-surface-500">Manage your account preferences and features</p>
      </div>

      {/* Mobile tab bar (horizontal scroll) */}
      <div className="lg:hidden mb-6 -mx-4 px-4 overflow-x-auto scrollbar-hide">
        <div className="flex gap-1 min-w-max bg-surface-100 rounded-xl p-1">
          {tabs.map((tab) => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`flex items-center gap-2 px-3.5 py-2 text-body-sm font-medium rounded-lg transition-all duration-200 whitespace-nowrap ${
                activeTab === tab.id
                  ? 'bg-white text-primary-600 shadow-soft-sm'
                  : 'text-surface-500 hover:text-surface-700'
              }`}
            >
              {tab.icon}
              <span className="hidden sm:inline">{tab.label}</span>
            </button>
          ))}
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-4 gap-8">
        {/* Desktop sidebar */}
        <div className="hidden lg:block lg:col-span-1">
          <div className="bg-white rounded-2xl shadow-soft-sm border border-surface-200 overflow-hidden sticky top-24">
            <nav className="py-2">
              {tabs.map((tab) => (
                <button
                  key={tab.id}
                  onClick={() => setActiveTab(tab.id)}
                  className={`w-full flex items-center gap-3 px-5 py-3 text-body-sm font-medium transition-colors duration-200 ${
                    activeTab === tab.id
                      ? 'bg-primary-50 text-primary-700 border-r-2 border-primary-500'
                      : 'text-surface-600 hover:bg-surface-50 hover:text-surface-800'
                  }`}
                >
                  <span className={activeTab === tab.id ? 'text-primary-500' : 'text-surface-400'}>
                    {tab.icon}
                  </span>
                  {tab.label}
                </button>
              ))}
            </nav>
          </div>
        </div>

        {/* Main content */}
        <div className="lg:col-span-3">
          <AnimatePresence mode="wait">
            {renderTabContent()}
          </AnimatePresence>
        </div>
      </div>
    </div>
  );
}

export default SettingsPage;
